/**
 * Response Handler
 * Handles API response processing and normalization
 */

import type { FrappeListResponse } from "./types";
import { logger } from "@/lib/utils/logger";
import { stripHtmlTags } from "@/lib/api/tenantServerClient";

export class ResponseHandler {
  /**
   * Handle standard API response
   */
  async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const error = await response.json();
        errorMessage = stripHtmlTags(error.message || error.exc || error.exc_type || errorMessage);
        logger.error("Frappe API error:", {
          status: response.status,
          statusText: response.statusText,
          error,
        });
      } catch (e) {
        const text = await response.text().catch(() => "");
        errorMessage = `HTTP error! status: ${response.status}`;
        logger.error("Frappe API error (non-JSON):", {
          status: response.status,
          statusText: response.statusText,
          body: text,
        });
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Handle list response with total count support
   */
  async handleListResponse<T>(
    response: Response,
    limit_page_length?: number,
    doctype?: string,
    getCountFn?: (doctype: string, filters?: Record<string, any>) => Promise<number>,
    filters?: Record<string, any>
  ): Promise<FrappeListResponse<T>> {
    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const error = await response.json();
        errorMessage = stripHtmlTags(error.message || error.exc || error.exc_type || errorMessage);
        logger.error("Frappe API error:", {
          status: response.status,
          statusText: response.statusText,
          error,
        });
      } catch (e) {
        const text = await response.text().catch(() => "");
        errorMessage = `HTTP error! status: ${response.status}`;
        logger.error("Frappe API error (non-JSON):", {
          status: response.status,
          statusText: response.statusText,
          body: text,
        });
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    const items = data.data || data || [];

    // Frappe API may return total in different places:
    // 1. data.total_count (if using frappe.client.get_list)
    // 2. response header X-Total-Count (custom)
    // 3. data.total (if custom endpoint)
    // 4. We may need to make a separate count query
    let total: number | null = null;

    // Try multiple sources for total count
    if (data.total_count !== undefined) {
      total = typeof data.total_count === "number" ? data.total_count : null;
    } else if (data.total !== undefined) {
      total = typeof data.total === "number" ? data.total : null;
    } else {
      const headerTotal = response.headers.get("X-Total-Count");
      if (headerTotal) {
        total = parseInt(headerTotal, 10);
        if (isNaN(total)) {
          total = null;
        }
      }
    }

    // ✅ Track if we're about to use fallback
    let usedItemsLengthAsFallback = false;

    // If no total found, try to get count via separate API call
    if (total === null || total === undefined) {
      if (doctype && filters && getCountFn) {
        try {
          total = await getCountFn(doctype, filters);
        } catch (error) {
          logger.error("Error getting count from Frappe:", error);
          // ✅ Mark that we'll use fallback
          usedItemsLengthAsFallback = true;
          total = items.length;
        }
      } else {
        // ✅ Mark that we'll use fallback
        usedItemsLengthAsFallback = true;
        total = items.length;
      }
    }

    // ✅ Log warning when using fallback
    if (usedItemsLengthAsFallback) {
      logger.warn("[ResponseHandler] Using items.length as total fallback", {
        doctype: doctype || "unknown",
        hasGetCountFn: Boolean(getCountFn),
        itemsCount: items.length,
        hint: "Backend did not return total_count/total/X-Total-Count. This may indicate a misconfigured endpoint or backend issue.",
      });
      
      // Log detailed filter info in debug mode if available
      if (filters) {
        logger.log("[ResponseHandler] Fallback count filters detail", { filters });
      }
    }

    return {
      data: items,
      total: typeof total === "number" ? total : items.length,
      pageLength: limit_page_length || items.length,
    };
  }
}

